import { motion } from 'framer-motion';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import { Badge } from './ui/badge';

// Animation constants - extracted to avoid inline object recreation
export const fadeInAnimation = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

export const fadeInDelayed = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
};

/**
 * Stat Card component for displaying metrics
 */
export function StatCard({ title, value, icon: Icon, trend, trendValue, subtitle, color = 'blue' }) {
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  };

  const colorStyle = colorClasses[color] || colorClasses.blue;

  return (
    <motion.div {...fadeInAnimation} className="dashboard-card p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend === 'up' ? <CaretUp size={16} weight="fill" /> : <CaretDown size={16} weight="fill" />}
              <span className="ml-1">{trendValue}</span>
            </div>
          )}
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorStyle.bg}`}>
          <Icon size={24} weight="duotone" className={colorStyle.text} />
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Status badge styles mapping
 */
const statusStyles = {
  verified: 'status-verified',
  pending: 'status-pending',
  sent: 'status-sent',
  failed: 'status-failed',
  expired: 'status-expired',
  rejected: 'status-failed',
};

/**
 * Status Badge component
 */
export function StatusBadge({ status }) {
  const style = statusStyles[status] || 'status-pending';
  return (
    <Badge className={`${style} text-xs font-medium`}>
      {status}
    </Badge>
  );
}

/**
 * Active/Inactive Status Badge
 */
export function ActiveBadge({ isActive }) {
  return (
    <Badge className={isActive ? 'status-verified' : 'status-failed'}>
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

/**
 * Loading Spinner component
 */
export function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2545]"></div>
    </div>
  );
}

/**
 * Dashboard Header component
 */
export function DashboardHeader({ title, subtitle, userName, onLogout, icon: Icon }) {
  return (
    <header className="header-glass sticky top-0 z-50 px-6 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2545] rounded-lg flex items-center justify-center">
            <Icon size={22} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">{title}</h1>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-600">{userName}</span>
          <button
            onClick={onLogout}
            className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="logout-button"
          >
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
}
